import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Anchor, Archive, KeyRound, LogOut, ShieldCheck, Loader2, X, Siren,
  PhoneOff, Navigation, Gauge, MapPin, Phone, User, Waves, AlertTriangle, Ship, Plane,
  CloudSun, Wind, Navigation2, Thermometer, Cloud, ChevronUp, ChevronDown, Volume2, VolumeX,
  Mic, MicOff,
} from "lucide-react";
import api, { apiErrorMessage, normalizeEvent, API_ORIGIN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startSiren, stopSiren } from "@/utils/siren";
import { formatCoords, formatCourse, formatSpeed, courseToText, distanceNm, bearingDeg } from "@/utils/geo";
import { useOperatorCalls } from "@/hooks/useOperatorCalls";
import { APP_VERSION } from "@/version";
import MapView from "@/components/MapView";
import { NearbyUnits, computeNearby } from "@/components/NearbyUnits";
import { fetchVessels } from "@/lib/ais";
import { recordCall } from "@/lib/calls";
import WindyOverlay from "@/components/WindyOverlay";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BRAND } from "@/branding";

const WINDY_LAYERS = [
  { code: "wind", label: "Vento", icon: Wind },
  { code: "waves", label: "Altezza onde", icon: Waves },
  { code: "wwaves", label: "Onde vento", icon: Waves },
  { code: "currents", label: "Correnti", icon: Navigation2 },
  { code: "temp", label: "Temperatura", icon: Thermometer },
  { code: "clouds", label: "Nuvole", icon: Cloud },
];

// "online" = ha inviato posizione negli ultimi minuti → mostrata sulla mappa.
// "assegnabile" = posizionata di recente (finestra più larga) → mostrata nel
// dialog di assegnazione anche se il ping è meno frequente, così l'assegnazione
// non fallisce mai per un filtro troppo stretto.
const MOTO_ONLINE_MS = 5 * 60 * 1000;
const MOTO_ASSIGNABLE_MS = 30 * 60 * 1000;
function motoAgeMs(mv) {
  if (!mv || !mv.last_pos || mv.last_seen == null) return Infinity;
  const t = Date.parse(mv.last_seen);
  return Number.isFinite(t) ? (Date.now() - t) : Infinity;
}
function isMotovedettaOnline(mv) {
  return motoAgeMs(mv) < MOTO_ONLINE_MS;
}

function TopBar({ onArchive, onChangePw, onLogout, username, isSupervisor }) {
  return (
    <header className="min-h-16 shrink-0 bg-[#0A2647] border-b border-[#1E5AA8] flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 py-2 z-[1200] relative">
      <div className="flex items-center gap-3">
        {/* Logo e scritte nascosti temporaneamente (restano nel codice). */}
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-2 mr-1">
          <span className="font-mono text-sm text-white" data-testid="topbar-username">{username}</span>
          {isSupervisor && (
            <Badge data-testid="supervisor-badge"
              className="bg-transparent border border-[#E8B923] text-[#E8B923] rounded-sm uppercase text-[10px] tracking-wider">
              <ShieldCheck className="h-3 w-3 mr-1" /> Supervisore
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onArchive} data-testid="nav-archive"
          className="border-[#1E5AA8] bg-transparent text-white hover:bg-[#1E5AA8] rounded-sm">
          <Archive className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Archivio</span>
        </Button>
        <Button variant="outline" size="sm" onClick={onChangePw} data-testid="nav-change-password"
          className="border-[#1E5AA8] bg-transparent text-white hover:bg-[#1E5AA8] rounded-sm">
          <KeyRound className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Password</span>
        </Button>
        <Button variant="outline" size="sm" onClick={onLogout} data-testid="nav-logout"
          className="border-[#D32F2F] bg-transparent text-[#ff8a8a] hover:bg-[#D32F2F] hover:text-white rounded-sm">
          <LogOut className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Esci</span>
        </Button>
      </div>
    </header>
  );
}

// Non-blocking panel showing ALL pending SOS at once with uniform styling, so
// the operator can see every active emergency simultaneously (and the map
// underneath) even during a burst of 10+ concurrent alarms.
function AlarmPanel({ events, onTake, takingId, onDismiss }) {
  if (!events.length) return null;
  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[1400] w-full max-w-md px-3"
      data-testid="alarm-panel" role="alertdialog" aria-label="Richieste di soccorso attive">
      <div className="alarm-modal bg-[#1E293B] border-4 border-[#D32F2F] rounded-sm shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 bg-[#D32F2F]/20 border-b border-[#D32F2F]">
          <Siren className="h-6 w-6 text-[#D32F2F] animate-pulse shrink-0" />
          <h2 className="font-heading font-black text-base sm:text-lg text-white uppercase tracking-wide flex-1 leading-tight">
            {events.length === 1 ? "Richiesta di soccorso" : "Richieste di soccorso"}
          </h2>
          <span className="font-mono font-black text-xl text-[#E8B923]" data-testid="alarm-count">{events.length}</span>
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-[#1E5AA8]/40">
          {events.map((ev) => (
            <div key={ev.id} className="px-4 py-3" data-testid={`alarm-item-${ev.id}`}>
              <p className="text-white text-sm mb-0.5" data-testid={`alarm-caller-${ev.id}`}>
                <span className="font-bold text-[#E8B923]">{ev.nome} {ev.cognome}</span>
                {ev.telefono ? <span className="font-mono"> · {ev.telefono}</span> : null}
              </p>
              <p className="text-xs text-[#A3C2E0] mb-2">{ev.zone_name}</p>
              <div className="flex gap-2">
                <Button onClick={() => onTake(ev)} disabled={takingId === ev.id} data-testid={`alarm-take-${ev.id}`}
                  className="flex-1 h-9 bg-[#D32F2F] hover:bg-[#b71c1c] text-white font-heading font-bold text-sm rounded-sm">
                  {takingId === ev.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Prendi in carico"}
                </Button>
                <Button onClick={() => onDismiss(ev)} variant="outline" data-testid={`alarm-dismiss-${ev.id}`}
                  className="h-9 px-3 border-[#1E5AA8] bg-transparent text-[#A3C2E0] hover:bg-[#1E5AA8] hover:text-white rounded-sm text-xs">
                  Ignora
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CallEndedModal({ event, onClose, onContinue, busy }) {
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="bg-[#1E293B] border-2 border-[#E8B923] text-white rounded-sm z-[1450]"
        data-testid="call-ended-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-[#E8B923]">
            <PhoneOff className="h-5 w-5" /> L'utente ha terminato la chiamata
          </DialogTitle>
          <DialogDescription className="text-[#A3C2E0]">
            Segnalante: <span className="text-white font-semibold">{event.nome} {event.cognome}</span> — {event.zone_name}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button onClick={() => onContinue(event)} disabled={busy} data-testid="call-ended-continue"
            variant="outline" className="border-[#1E5AA8] bg-transparent text-white hover:bg-[#1E5AA8] rounded-sm">
            Continuare
          </Button>
          <Button onClick={() => onClose(event)} disabled={busy} data-testid="call-ended-close"
            className="bg-[#D32F2F] hover:bg-[#b71c1c] text-white rounded-sm">
            Chiudere il soccorso
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DataRow({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-[#1E5AA8]/40 last:border-0">
      <Icon className="h-4 w-4 text-[#A3C2E0] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#A3C2E0]">{label}</div>
        <div className="font-mono text-sm text-white break-words" data-testid={testid}>{value}</div>
      </div>
    </div>
  );
}

// Pulsante muta riutilizzabile (per ruolo o per microfono operatore).
function MuteButton({ label, muted, mic, speaking, onClick, testid }) {
  const Icon = mic ? (muted ? MicOff : Mic) : (muted ? VolumeX : Volume2);
  const text = mic
    ? (muted ? "Riattiva microfono" : "Muta microfono")
    : (muted ? `Riattiva ${label}` : `Muta ${label}`);
  return (
    <Button onClick={onClick} data-testid={testid}
      className={`w-full h-9 rounded-sm font-heading font-bold text-sm justify-start ${speaking ? "animate-pulse" : ""} ${muted
        ? "bg-[#E8B923] hover:bg-[#cfa415] text-[#0A2647]"
        : "bg-transparent border border-[#1E5AA8] text-white hover:bg-[#1E5AA8]"}`}>
      <Icon className="h-4 w-4 mr-1.5 shrink-0" /> {text}
    </Button>
  );
}

// Dialog per assegnare una motovedetta a un soccorso.
function AssignMotovedettaDialog({ event, motovedette, onAssign, onClose, busy, assignedTo }) {
  return (
    <Dialog open={!!event} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="assign-motovedetta-dialog" className="bg-[#0A2647] border-[#1E5AA8] text-white rounded-sm z-[1450]">
        <DialogHeader>
          <DialogTitle className="font-heading">Assegna motovedetta</DialogTitle>
          <DialogDescription className="text-[#A3C2E0]">
            {event ? `Soccorso: ${event.nome} ${event.cognome}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-2">
          {(motovedette || []).length === 0 && (
            <p className="text-sm text-[#A3C2E0]" data-testid="assign-empty">Nessuna motovedetta disponibile.</p>
          )}
          {(motovedette || []).map((mv) => {
            const busyOn = (assignedTo || {})[mv.id];
            return (
              <button key={mv.id} onClick={() => onAssign(mv.id)} disabled={busy}
                data-testid={`assign-moto-${mv.id}`}
                className={`w-full text-left px-3 py-2 rounded-sm border bg-[#113259] hover:bg-[#1E5AA8] transition-colors flex items-center gap-2 disabled:opacity-60 border-l-4 ${busyOn ? "border-[#1E5AA8] border-l-[#E8B923]" : "border-[#1E5AA8] border-l-[#2E7D32]"}`}>
                <Ship className={`h-4 w-4 shrink-0 ${busyOn ? "text-[#E8B923]" : "text-[#7ee08a]"}`} />
                <span className="text-sm flex-1 min-w-0">
                  <span className="font-semibold text-white">{mv.username || mv.name || mv.id}</span>
                  {mv.zone_name ? <span className="text-[#A3C2E0]"> · {mv.zone_name}</span> : null}
                </span>
                <span data-testid={`assign-moto-state-${mv.id}`}
                  className={`text-[10px] font-mono font-semibold shrink-0 px-1.5 py-0.5 rounded-sm border ${busyOn
                    ? "text-[#E8B923] border-[#E8B923]/60 bg-[#E8B923]/15"
                    : "text-[#7ee08a] border-[#2E7D32]/60 bg-[#2E7D32]/20"}`}>
                  {busyOn ? `Assegnata #${busyOn}` : "Libera"}
                </span>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}
            className="border-[#1E5AA8] bg-transparent text-white hover:bg-[#1E5AA8]">Annulla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CallerPanel({ event, onClose, onEndRequest, call, onMuteRole, onToggleMic, onMinimize, onAssign, nearby, routeInfo }) {
  const last = event.last || {};
  const isActive = event.status === "active";
  // MRSC di competenza SENZA città (es. "3° MRSC").
  const mrsc = event.zone_number
    ? `${event.zone_number}° MRSC`
    : (event.zone_name || "").replace(/\s+\S+$/, "");
  const hasCall = call && (call.status === "in-call" || call.status === "connecting");
  const callerMuted = !!(call && call.callerMuted);
  const rescuerMuted = !!(call && call.rescuerMuted);
  const micMuted = !!(call && call.micMuted);
  const rescuerPresent = !!(call && call.rescuerPresent);
  const inCall = !!(call && call.status === "in-call");
  // L'icona di intestazione riflette l'audio del naufrago (caller).
  const muted = callerMuted;
  const speaking = !!(call && call.callerSpeaking && !callerMuted);
  return (
    <div className="w-full bg-[#113259] border border-[#1E5AA8] rounded-sm shadow-2xl"
      data-testid="caller-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E5AA8] bg-[#0A2647]">
        <div className="flex items-center gap-2 min-w-0">
          {inCall ? (
            <span className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${muted ? "bg-[#D32F2F]" : "bg-[#22a35a]"} ${speaking ? "animate-pulse" : ""}`}
              data-testid="caller-panel-call-icon">
              {muted ? <VolumeX className="h-3 w-3 text-white" /> : <Phone className="h-3 w-3 text-white" />}
            </span>
          ) : (
            <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${isActive ? "bg-[#D32F2F]" : "bg-[#E8B923]"}`} />
          )}
          {mrsc && (
            <span className="shrink-0 text-[11px] font-heading font-bold text-[#E8B923] border border-[#E8B923] rounded-sm px-1.5 py-0.5 uppercase tracking-wide"
              data-testid="caller-panel-mrsc">{mrsc}</span>
          )}
          <span className="font-heading font-bold text-white text-sm uppercase tracking-wide truncate">
            {isActive ? "Segnalante attivo" : "Soccorso in carico"}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={onMinimize} data-testid="caller-panel-minimize"
            aria-label="Minimizza pannello" className="text-[#A3C2E0] hover:text-white transition-colors">
            <ChevronUp className="h-4 w-4" />
          </button>
          <button onClick={onClose} aria-label="Chiudi pannello" data-testid="caller-panel-close"
            className="text-[#A3C2E0] hover:text-white"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="px-4 py-2">
        <DataRow icon={User} label="Nome" value={event.nome} testid="panel-nome" />
        <DataRow icon={User} label="Cognome" value={event.cognome} testid="panel-cognome" />
        <DataRow icon={Phone} label="Telefono" value={event.telefono} testid="panel-telefono" />
        <DataRow icon={MapPin} label="Coordinate (DMS)" value={formatCoords(last.lat, last.lng)} testid="panel-coords" />
        <DataRow icon={Navigation} label="Rotta" value={formatCourse(last.course)} testid="panel-course" />
        <DataRow icon={Gauge} label="Velocità" value={formatSpeed(last.speed)} testid="panel-speed" />
        {event.assigned_motovedetta_name && (
          <DataRow icon={Ship} label="Motovedetta" value={event.assigned_motovedetta_name} testid="panel-motovedetta" />
        )}
        {routeInfo && (
          <DataRow icon={Navigation} label="Rotta motovedetta → segnalante"
            value={`${Math.round(routeInfo.bearing)}° ${courseToText(routeInfo.bearing)} · ${routeInfo.dist.toFixed(1)} NM · ETA ${routeInfo.eta}`}
            testid="panel-route-eta" />
        )}
        {event.status === "taken" && (
          <div className="pt-3 pb-1">
            <Button onClick={() => onAssign(event)} data-testid="panel-assign-motovedetta"
              className="w-full h-9 rounded-sm font-heading font-bold text-sm bg-transparent border border-[#1E5AA8] text-white hover:bg-[#1E5AA8]">
              <Ship className="h-4 w-4 mr-1.5" /> {event.assigned_motovedetta_name ? "Riassegna motovedetta" : "Assegna motovedetta"}
            </Button>
          </div>
        )}
        {nearby && (
          <NearbyUnits units={nearby.units} radiusNm={nearby.radiusNm} onRadiusChange={nearby.setRadiusNm}
            onAssign={nearby.assign} onHover={nearby.setHover} canAssign={event.status === "taken"} />
        )}
        {hasCall && (
          <div className="pt-3 pb-1 space-y-2" data-testid="call-mute-controls">
            <MuteButton label="naufrago" muted={callerMuted} speaking={speaking}
              onClick={() => onMuteRole(event.id, "caller", !callerMuted)} testid="panel-mute-naufrago" />
            {rescuerPresent && (
              <MuteButton label="motovedetta" muted={rescuerMuted}
                speaking={!!(call && call.rescuerSpeaking && !rescuerMuted)}
                onClick={() => onMuteRole(event.id, "rescuer", !rescuerMuted)} testid="panel-mute-motovedetta" />
            )}
            <MuteButton mic muted={micMuted}
              onClick={() => onToggleMic(event.id)} testid="panel-mute-self" />
          </div>
        )}
      </div>
      {event.status === "taken" && (
        <div className="p-3 border-t border-[#1E5AA8]">
          <Button onClick={() => onEndRequest(event)} data-testid="panel-end-emergency"
            className="w-full bg-[#D32F2F] hover:bg-[#b71c1c] text-white font-heading font-bold rounded-sm">
            <AlertTriangle className="h-4 w-4 mr-1" /> Termine emergenza
          </Button>
        </div>
      )}
    </div>
  );
}

// Barra compatta per un soccorso aperto ma minimizzato (impilata sotto la
// finestra in primo piano). Cliccandola la finestra torna in primo piano.
function MinimizedPanel({ event, call, onExpand, onClose }) {
  const isActive = event.status === "active";
  const mrsc = event.zone_number
    ? `${event.zone_number}° MRSC`
    : (event.zone_name || "").replace(/\s+\S+$/, "");
  const inCall = !!(call && call.status === "in-call");
  const muted = !!(call && call.callerMuted);
  const speaking = !!(call && call.callerSpeaking && !muted);
  return (
    <div onClick={onExpand} data-testid={`caller-panel-mini-${event.id}`}
      className={`w-full bg-[#113259] border rounded-sm shadow-xl flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors ${speaking ? "sgc-mini-talking border-[#22a35a]" : "border-[#1E5AA8] hover:border-[#E8B923]"}`}>
      {inCall ? (
        <span className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${muted ? "bg-[#D32F2F]" : "bg-[#22a35a]"} ${speaking ? "animate-pulse" : ""}`}
          data-testid={`caller-panel-mini-icon-${event.id}`}>
          {muted ? <VolumeX className="h-3 w-3 text-white" /> : <Phone className="h-3 w-3 text-white" />}
        </span>
      ) : (
        <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${isActive ? "bg-[#D32F2F]" : "bg-[#E8B923]"}`} />
      )}
      {mrsc && (
        <span className="shrink-0 text-[10px] font-heading font-bold text-[#E8B923] border border-[#E8B923] rounded-sm px-1.5 py-0.5 uppercase tracking-wide">{mrsc}</span>
      )}
      <span className="text-xs text-[#A3C2E0] truncate flex-1 min-w-0">
        <span className="text-white font-semibold">{event.nome} {event.cognome}</span>
        {event.telefono ? ` · ${event.telefono}` : ""}
      </span>
      <button onClick={(e) => { e.stopPropagation(); onExpand(); }} data-testid={`caller-panel-expand-${event.id}`}
        aria-label="Apri pannello" className="text-[#A3C2E0] hover:text-white shrink-0"><ChevronDown className="h-4 w-4" /></button>
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid={`caller-panel-mini-close-${event.id}`}
        aria-label="Chiudi pannello" className="text-[#A3C2E0] hover:text-white shrink-0"><X className="h-4 w-4" /></button>
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const { changePassword } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pw.length < 3) return toast.error("Password troppo corta (min 3)");
    if (pw !== pw2) return toast.error("Le password non coincidono");
    setBusy(true);
    try {
      await changePassword(pw);
      toast.success("Password aggiornata");
      setPw(""); setPw2("");
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#113259] border border-[#1E5AA8] text-white rounded-sm z-[1500]" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Cambia password</DialogTitle>
          <DialogDescription className="text-[#A3C2E0]">Imposta una nuova password per il tuo account operatore.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-[#A3C2E0]">Nuova password</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              data-testid="new-password-input" className="bg-[#0A2647] border-[#1E5AA8] text-white" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#A3C2E0]">Conferma password</Label>
            <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
              data-testid="confirm-password-input" className="bg-[#0A2647] border-[#1E5AA8] text-white" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="change-password-submit"
            className="bg-[#E8B923] hover:bg-[#cfa415] text-[#0A2647] font-bold rounded-sm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiorna"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DashboardPage() {
  const { auth, logout, isSupervisor } = useAuth();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openIds, setOpenIds] = useState([]);   // soccorsi aperti (finestre)
  const [activeId, setActiveId] = useState(null); // finestra in primo piano
  const selectedIdRef = useRef(null);
  const seenIdsRef = useRef(null);
  const fetchingRef = useRef(false);
  const [takingId, setTakingId] = useState(null);
  const [motovedette, setMotovedette] = useState([]);
  const [assignEvent, setAssignEvent] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(null);
  const [alarmDismissed, setAlarmDismissed] = useState({});
  const [callDismissed, setCallDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sgc_call_dismissed") || "{}"); } catch (e) { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("sgc_call_dismissed", JSON.stringify(callDismissed)); } catch (e) {}
  }, [callDismissed]);
  const [iceServers, setIceServers] = useState([{ urls: "stun:stun.l.google.com:19302" }]);
  const [webrtcEnabled, setWebrtcEnabled] = useState(process.env.REACT_APP_WEBRTC_ENABLED === "true");
  const [mapView, setMapView] = useState(null);
  const [windyEnabled, setWindyEnabled] = useState(false);
  const [windyOverlay, setWindyOverlay] = useState("wind");
  const [windyOpacity, setWindyOpacity] = useState(70);
  const [showAis, setShowAis] = useState(false);
  const [nearbyRadius, setNearbyRadius] = useState(5);
  const [nearbyVessels, setNearbyVessels] = useState([]);
  const [nearbyHover, setNearbyHover] = useState(null);
  const [aisOnlyService, setAisOnlyService] = useState(false);
  const [aisInfo, setAisInfo] = useState({ active: false, count: 0, zoomLow: false });
  const [showFlights, setShowFlights] = useState(false);
  const [flightsOnlySpecial, setFlightsOnlySpecial] = useState(false);
  const [flightsInfo, setFlightsInfo] = useState({ active: false, count: 0 });
  const [motoPanelOpen, setMotoPanelOpen] = useState(false);
  const [windyMin, setWindyMin] = useState(false);

  // Incoming audio calls: every active event with an open call line from the
  // app (independent of map selection) so calls ring automatically. Multiple
  // simultaneous calls are supported (one card each in the CallDock).
  const callEvents = useMemo(() =>
    events
      .filter((e) =>
        e.call_active && !e.call_ended_by_user &&
        e.status !== "ended" && e.status !== "closed" &&
        !callDismissed[e.id])
      .map((e) => ({
        id: e.id, nome: e.nome, cognome: e.cognome, number: e.number, year: e.year,
        zone_name: e.zone_name, operator_username: e.operator_username,
      })),
    [events, callDismissed]);

  const call = useOperatorCalls({
    apiOrigin: API_ORIGIN,
    enabled: webrtcEnabled,
    iceServers,
    callEvents,
    onCallEnded: recordCall,
    onDismiss: (id) => setCallDismissed((d) => ({ ...d, [id]: true })),
  });

  const activeEvent = useMemo(() => {
    if (!activeId) return null;
    if (selectedEvent && selectedEvent.id === activeId) return selectedEvent;
    return events.find((e) => e.id === activeId) || null;
  }, [activeId, selectedEvent, events]);

  const activeCall = useMemo(
    () => (call.calls || []).find((c) => c.eventId === activeId),
    [call.calls, activeId]
  );
  const minimizedEvents = useMemo(
    () => openIds.filter((id) => id !== activeId)
      .map((id) => events.find((e) => e.id === id)).filter(Boolean),
    [openIds, activeId, events]
  );

  // Apri (o porta in primo piano) la finestra di un soccorso.
  const activatePanel = useCallback(async (id) => {
    setOpenIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setActiveId(id);
    selectedIdRef.current = id;
    try {
      const { data } = await api.get(`/events/${id}`);
      if (selectedIdRef.current === id) setSelectedEvent(normalizeEvent(data));
    } catch (e) {}
  }, []);
  // Minimizza la finestra in primo piano (resta nella pila).
  const minimizePanel = useCallback(() => {
    setActiveId(null);
    selectedIdRef.current = null;
    setSelectedEvent(null);
  }, []);
  // Chiudi del tutto una finestra.
  const closePanel = useCallback((id) => {
    setOpenIds((prev) => prev.filter((x) => x !== id));
    setActiveId((cur) => (cur === id ? null : cur));
    if (selectedIdRef.current === id) { selectedIdRef.current = null; setSelectedEvent(null); }
  }, []);
  // Rimuovi dalle finestre aperte i soccorsi non più presenti (terminati).
  useEffect(() => {
    setOpenIds((prev) => {
      const present = prev.filter((id) => events.some((e) => e.id === id));
      return present.length === prev.length ? prev : present;
    });
    if (activeId && !events.some((e) => e.id === activeId)) {
      setActiveId(null);
      if (selectedIdRef.current === activeId) { selectedIdRef.current = null; setSelectedEvent(null); }
    }
  }, [events, activeId]);

  // Map marker + pannelli: info chiamata per evento.
  const callByEventId = useMemo(() => {
    const m = {};
    (call.calls || []).forEach((c) => { m[c.eventId] = c; });
    return m;
  }, [call.calls]);
  const callInfoById = useMemo(() => {
    const m = {};
    (call.calls || []).forEach((c) => {
      m[c.eventId] = { inCall: c.status === "in-call", muted: !!c.callerMuted, speaking: !!c.callerSpeaking };
    });
    return m;
  }, [call.calls]);

  // Mappa: solo motovedette online (posizione degli ultimi minuti).
  const onlineMotovedette = useMemo(
    () => (motovedette || []).filter(isMotovedettaOnline),
    [motovedette]
  );
  const sortedMotovedette = useMemo(
    () => [...(motovedette || [])].sort((a, b) => motoAgeMs(a) - motoAgeMs(b)),
    [motovedette]
  );
  // Dialog assegnazione: motovedette posizionate di recente (finestra più larga)
  // così restano selezionabili anche con ping meno frequente.
  const assignableMotovedette = useMemo(
    () => (motovedette || []).filter((m) => m.last_pos && motoAgeMs(m) < MOTO_ASSIGNABLE_MS),
    [motovedette]
  );
  const onlineMotoIds = useMemo(
    () => new Set(onlineMotovedette.map((m) => m.id)),
    [onlineMotovedette]
  );
  const assignedMotoIds = useMemo(
    () => new Set((events || []).map((e) => e.assigned_motovedetta_id).filter(Boolean)),
    [events]
  );
  const assignedToByMoto = useMemo(() => {
    const m = {};
    (events || []).forEach((e) => { if (e.assigned_motovedetta_id) m[e.assigned_motovedetta_id] = e.number; });
    return m;
  }, [events]);

  const focusZoneNumbers = useMemo(() => {
    if (isSupervisor) return undefined;
    const mine = zones.filter((z) => z.operator === auth?.username).map((z) => z.number);
    return mine.length ? mine : undefined;
  }, [zones, isSupervisor, auth]);

  const windyCenter = useMemo(() => {
    if (!isSupervisor && focusZoneNumbers && zones.length) {
      const z = zones.find((zz) => focusZoneNumbers.includes(zz.number));
      if (z && z.points && z.points.length) {
        const lat = z.points.reduce((a, p) => a + p[0], 0) / z.points.length;
        const lng = z.points.reduce((a, p) => a + p[1], 0) / z.points.length;
        return { lat, lng, zoom: 8 };
      }
    }
    return { lat: 41.9, lng: 12.5, zoom: 6 };
  }, [isSupervisor, focusZoneNumbers, zones]);

  useEffect(() => {
    api.get("/zones").then(({ data }) => setZones(data)).catch((e) => setLoadError(apiErrorMessage(e)));
  }, []);

  useEffect(() => {
    api.get("/ice").then(({ data }) => {
      if (Array.isArray(data?.iceServers) && data.iceServers.length) {
        setIceServers(data.iceServers);
        setWebrtcEnabled(true);
      }
    }).catch(() => {});
  }, []);

  const fetchMotovedette = useCallback(async () => {
    try {
      const { data } = await api.get("/motovedette");
      setMotovedette(Array.isArray(data) ? data : []);
    } catch (e) { /* endpoint può non essere ancora attivo lato backend */ }
  }, []);
  useEffect(() => {
    fetchMotovedette();
    const t = setInterval(fetchMotovedette, 5000);
    return () => clearInterval(t);
  }, [fetchMotovedette]);

  const refreshSelected = useCallback(async () => {
    const id = selectedIdRef.current;
    if (!id) return;
    try {
      const { data } = await api.get(`/events/${id}`);
      // Selection could have been cleared (e.g. Termine emergenza) while this
      // request was in flight — don't repopulate a stale/closed event.
      if (selectedIdRef.current !== id) return;
      const ev = normalizeEvent(data);
      if (ev.status === "ended" || ev.status === "closed") {
        selectedIdRef.current = null;
        setSelectedEvent(null);
        return;
      }
      setSelectedEvent(ev);
    } catch (e) { /* keep last */ }
  }, []);

  // Desktop notifications for new SOS (fires when the tab is in background).
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const notifyNewSos = useCallback((list) => {
    const activeIds = list.filter((e) => e.status === "active").map((e) => e.id);
    if (seenIdsRef.current === null) { seenIdsRef.current = new Set(activeIds); return; }
    const fresh = list.filter((e) => e.status === "active" && !seenIdsRef.current.has(e.id));
    seenIdsRef.current = new Set(activeIds);
    if (fresh.length && typeof Notification !== "undefined" && Notification.permission === "granted" && document.hidden) {
      fresh.forEach((e) => {
        try {
          const n = new Notification(BRAND.notificationTitle, {
            body: `${e.nome || ""} ${e.cognome || ""} \u00B7 ${e.zone_name || "zona n/d"}`.trim(),
            tag: `sos-${e.id}`,
          });
          n.onclick = () => { try { window.focus(); } catch (err) {} n.close(); };
        } catch (err) {}
      });
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    // Anti-overlap guard: never stack requests if a poll is slow under load.
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const { data } = await api.get("/events", { params: { scope: "active" } });
      const normalized = (data || []).map(normalizeEvent);
      setEvents(normalized);
      notifyNewSos(normalized);
      setLoadError(null);
    } catch (e) {
      setLoadError(apiErrorMessage(e));
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEvents();
    const t = setInterval(() => { fetchEvents(); refreshSelected(); }, 3000);
    return () => clearInterval(t);
  }, [fetchEvents, refreshSelected]);

  // Ripristino audio dopo un ricaricamento pagina (es. CTRL+F5): il reload
  // distrugge la connessione WebRTC. Ri-agganciamo automaticamente le chiamate
  // dei soccorsi che QUESTO operatore ha preso in carico. answer() è idempotente
  // (no-op se la sessione esiste già), quindi può girare ad ogni poll.
  useEffect(() => {
    if (!webrtcEnabled) return;
    events.forEach((ev) => {
      if (ev.status === "taken" && ev.taken_by === auth?.username &&
          ev.call_active && !ev.call_ended_by_user && !callDismissed[ev.id]) {
        call.answer(ev.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, webrtcEnabled, auth]);

  // Alarms: ALL active untaken not-dismissed SOS (shown together in AlarmPanel).
  const pendingAlarms = useMemo(
    () => events.filter((e) => e.status === "active" && !alarmDismissed[e.id]),
    [events, alarmDismissed]
  );
  const hasPendingAlarms = pendingAlarms.length > 0;
  useEffect(() => {
    if (hasPendingAlarms) startSiren(); else stopSiren();
    return () => stopSiren();
  }, [hasPendingAlarms]);

  // Call ended modal
  const callEndedEvent = useMemo(
    () => events.find((e) => e.status === "taken" && e.call_ended_by_user && !e.operator_decision),
    [events]
  );

  // Safety net: i dialog Radix a volte lasciano document.body con
  // pointer-events:none dopo la chiusura durante un re-render asincrono,
  // congelando i clic sull'INTERA pagina (es. il pulsante "Assegna motovedetta"
  // non risponde più). Quando NESSUN modale dell'app è aperto, ripristiniamo i
  // clic in modo continuo (interval) così il body non resta mai bloccato.
  const anyModalOpen = !!confirmEnd || !!callEndedEvent || showPw || !!assignEvent;
  useEffect(() => {
    if (anyModalOpen) return;
    const clear = () => {
      if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    };
    clear();
    const t = setInterval(clear, 400);
    return () => clearInterval(t);
  }, [anyModalOpen]);


  const handleTake = async (ev) => {
    setTakingId(ev.id);
    try {
      await api.post(`/events/${ev.id}/take`);
      toast.success(`Preso in carico: ${ev.nome} ${ev.cognome}`);
      // Optimistic: remove from the pending panel immediately.
      setEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, status: "taken" } : e)));
      // Risposta automatica alla chiamata all'atto della presa in carico.
      if (ev.call_active && webrtcEnabled) call.answer(ev.id);
      await activatePanel(ev.id);
      await fetchEvents();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setTakingId(null); }
  };

  // Unità vicine: navi AIS di servizio attorno all'evento attivo (bbox = raggio scelto).
  const nearbyLat = activeEvent && activeEvent.last ? activeEvent.last.lat : null;
  const nearbyLng = activeEvent && activeEvent.last ? activeEvent.last.lng : null;
  useEffect(() => {
    if (nearbyLat == null) { setNearbyVessels([]); return; }
    let cancelled = false;
    const load = async () => {
      const dLat = nearbyRadius / 60;
      const dLng = nearbyRadius / (60 * Math.max(0.2, Math.cos((nearbyLat * Math.PI) / 180)));
      try {
        const data = await fetchVessels({
          min_lat: nearbyLat - dLat, max_lat: nearbyLat + dLat,
          min_lng: nearbyLng - dLng, max_lng: nearbyLng + dLng, category: "service",
        });
        if (!cancelled) setNearbyVessels(data.vessels || []);
      } catch (e) { if (!cancelled) setNearbyVessels([]); }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [nearbyLat, nearbyLng, nearbyRadius]);

  const nearbyUnits = useMemo(() => {
    if (!activeEvent || !activeEvent.last) return [];
    const mvs = motovedette.map((mv) => {
      if (mv.id === activeEvent.assigned_motovedetta_id) return { ...mv, _assignedTo: activeEvent.number, _here: true };
      const busy = events.find((e) => e.assigned_motovedetta_id === mv.id && e.id !== activeEvent.id);
      return { ...mv, _assignedTo: busy ? busy.number : null };
    });
    return computeNearby(activeEvent, mvs, nearbyVessels, nearbyRadius);
  }, [activeEvent, motovedette, events, nearbyVessels, nearbyRadius]);

  const nearbyAssign = useCallback(async (motoId) => {
    if (!activeEvent) return;
    const eid = activeEvent.id;
    setAssigning(true);
    try {
      await api.post(`/events/${eid}/assign`, { motovedetta_id: motoId });
      toast.success("Motovedetta assegnata");
      await fetchEvents(); await fetchMotovedette();
      if (selectedIdRef.current === eid) await activatePanel(eid);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setAssigning(false); }
  }, [activeEvent, fetchEvents, fetchMotovedette, activatePanel]);

  const nearbyCtx = useMemo(() => (activeEvent && activeEvent.last ? {
    units: nearbyUnits, radiusNm: nearbyRadius, setRadiusNm: setNearbyRadius, setHover: setNearbyHover, assign: nearbyAssign,
  } : null), [activeEvent, nearbyUnits, nearbyRadius, nearbyAssign]);

  // Rotta e ETA della motovedetta assegnata verso il segnalante (aggiornati a ogni polling).
  const routeInfo = useMemo(() => {
    if (!activeEvent || !activeEvent.last || !activeEvent.assigned_motovedetta_id) return null;
    const mv = motovedette.find((m) => m.id === activeEvent.assigned_motovedetta_id);
    if (!mv || !mv.last_pos || mv.last_pos.lat == null) return null;
    const dist = distanceNm(mv.last_pos.lat, mv.last_pos.lng, activeEvent.last.lat, activeEvent.last.lng);
    const bearing = bearingDeg(mv.last_pos.lat, mv.last_pos.lng, activeEvent.last.lat, activeEvent.last.lng);
    const moving = (mv.last_pos.speed || 0) > 0.5;
    const kn = moving ? mv.last_pos.speed : 25;
    const min = Math.round((dist / kn) * 60);
    const eta = (min < 60 ? `${min} min` : `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}`) + (moving ? "" : " (stima 25 kn)");
    return { dist, bearing, eta };
  }, [activeEvent, motovedette]);

  const nearbyMap = useMemo(() => (activeEvent && activeEvent.last ? {
    eventId: activeEvent.id, lat: activeEvent.last.lat, lng: activeEvent.last.lng, radiusNm: nearbyRadius,
    status: activeEvent.status, units: nearbyUnits, hover: nearbyHover,
  } : null), [activeEvent, nearbyRadius, nearbyUnits, nearbyHover]);

  const handleAssign = async (motoId) => {
    if (!assignEvent) return;
    setAssigning(true);
    const eid = assignEvent.id;
    try {
      await api.post(`/events/${eid}/assign`, { motovedetta_id: motoId });
      toast.success("Motovedetta assegnata");
      setAssignEvent(null);
      await fetchEvents(); await fetchMotovedette();
      if (selectedIdRef.current === eid) await activatePanel(eid);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setAssigning(false); }
  };

  const handleMarkerClick = (ev) => { activatePanel(ev.id); };

  const handleContinue = async (ev) => {
    setBusy(true);
    try {
      await api.post(`/events/${ev.id}/continue`);
      toast.success("Soccorso in continuazione");
      await fetchEvents(); await activatePanel(ev.id);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally {
      setBusy(false);
      if (typeof document !== "undefined") document.body.style.pointerEvents = "";
    }
  };

  const handleCloseFromCallEnded = async (ev) => {
    setBusy(true);
    closePanel(ev.id);
    try {
      await api.post(`/events/${ev.id}/end-emergency`);
      toast.success("Soccorso chiuso");
      await fetchEvents();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally {
      setBusy(false);
      if (typeof document !== "undefined") document.body.style.pointerEvents = "";
    }
  };

  const doEndEmergency = async () => {
    const ev = confirmEnd;
    if (!ev) return;
    const id = ev.id || selectedIdRef.current;
    setConfirmEnd(null);
    if (!id) { toast.error("Evento non valido, riprova"); return; }
    // Chiudi la finestra up-front così il poll da 3s non ripopola il pannello
    // con l'evento (che sta per essere chiuso) mentre la richiesta è in volo.
    closePanel(id);
    setBusy(true);
    try {
      await api.post(`/events/${id}/end-emergency`);
      toast.success("Emergenza terminata");
      await fetchEvents();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally {
      setBusy(false);
      if (typeof document !== "undefined") document.body.style.pointerEvents = "";
    }
  };

  return (
    <div className="w-full flex flex-col overflow-hidden" style={{ height: BRAND.banner ? "calc(100vh - 28px)" : "100vh" }}>
      <TopBar
        username={auth?.username}
        isSupervisor={isSupervisor}
        onArchive={() => navigate("/archivio")}
        onChangePw={() => setShowPw(true)}
        onLogout={() => { stopSiren(); logout(); navigate("/login"); }}
      />

      <div className="flex-1 relative">
        {loadError && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1300] bg-[#1E293B] border border-[#D32F2F] text-[#ff8a8a] px-4 py-2 rounded-sm text-sm"
            data-testid="dashboard-error">
            {loadError}
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 z-[1300] flex items-center justify-center bg-[#0A2647]/70" data-testid="dashboard-loading">
            <Loader2 className="h-8 w-8 animate-spin text-[#E8B923]" />
          </div>
        )}

        <MapView
          zones={zones}
          events={events}
          selectedEvent={selectedEvent}
          onMarkerClick={handleMarkerClick}
          focusZoneNumbers={focusZoneNumbers}
          onViewChange={setMapView}
          maxZoom={windyEnabled ? 11 : 18}
          showAis={showAis}
          nearby={nearbyMap}
          aisOnlyService={aisOnlyService}
          showFlights={showFlights}
          flightsOnlySpecial={flightsOnlySpecial}
          onFlightsInfo={setFlightsInfo}
          onAisInfo={setAisInfo}
          callInfoById={callInfoById}
          motovedette={onlineMotovedette}
        />

        <WindyOverlay
          center={mapView || windyCenter}
          enabled={windyEnabled}
          overlay={windyOverlay}
          opacity={windyOpacity}
        />

        {/* Bottom-left map controls: Meteo + Navi AIS */}
        <div className="absolute bottom-4 left-4 z-[1150] flex flex-col gap-2 items-start w-60 max-w-[80vw]"
          data-testid="map-controls-left">
          {/* Meteo (single-row toggle) */}
          <div className="w-full bg-[#0A2647]/95 border border-[#1E5AA8] rounded-sm shadow-xl text-white"
            data-testid="windy-control">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="flex items-center gap-2 font-heading font-bold text-sm">
                <CloudSun className="h-4 w-4 text-[#E8B923]" /> Meteo
              </span>
              <div className="flex items-center gap-2">
                {windyEnabled && (
                  <button type="button" onClick={() => setWindyMin((m) => !m)}
                    data-testid="windy-minimize" aria-label="Minimizza finestra meteo"
                    className="text-[#A3C2E0] hover:text-white transition-colors">
                    {windyMin ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                  </button>
                )}
                <Switch checked={windyEnabled} onCheckedChange={setWindyEnabled} data-testid="windy-enable" />
              </div>
            </div>
            {windyEnabled && !windyMin && (
              <div className="px-3 pb-3 space-y-3 border-t border-[#1E5AA8]/50 pt-3">
                <div className="grid grid-cols-2 gap-1.5" data-testid="windy-layers">
                  {WINDY_LAYERS.map((l) => {
                    const Icon = l.icon;
                    const active = windyOverlay === l.code;
                    return (
                      <button key={l.code} onClick={() => setWindyOverlay(l.code)}
                        data-testid={`windy-layer-${l.code}`}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-sm text-xs border transition-colors ${
                          active ? "bg-[#E8B923] text-[#0A2647] border-[#E8B923] font-semibold"
                                  : "bg-transparent text-white border-[#1E5AA8] hover:bg-[#1E5AA8]"}`}>
                        <Icon className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{l.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-[#A3C2E0] uppercase tracking-wider">Opacità</span>
                    <span className="font-mono text-xs text-[#E8B923]" data-testid="windy-opacity-value">{windyOpacity}%</span>
                  </div>
                  <Slider value={[windyOpacity]} min={10} max={100} step={5}
                    onValueChange={(v) => setWindyOpacity(v[0])} data-testid="windy-opacity" aria-label="Opacità meteo" />
                </div>
                <p className="text-[10px] text-[#6f8fb3] leading-tight">
                  Il meteo segue lo zoom e la posizione della mappa (zoom max 11 con meteo attivo). I marker restano cliccabili sotto l'overlay.
                </p>
              </div>
            )}
          </div>

          {/* Navi AIS (single-row toggle) */}
          <div className="w-full bg-[#0A2647]/95 border border-[#1E5AA8] rounded-sm shadow-xl text-white"
            data-testid="ais-control">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="flex items-center gap-2 font-heading font-bold text-sm">
                <Ship className="h-4 w-4 text-[#22d3ee]" /> Navi AIS
              </span>
              <Switch checked={showAis} onCheckedChange={setShowAis} data-testid="ais-enable" />
            </div>
            {showAis && (
              <div className="px-3 pb-2 -mt-0.5 space-y-1.5">
                <div className="text-[10px] text-[#A3C2E0] leading-tight" data-testid="ais-status">
                  {aisInfo.zoomLow
                    ? "Ingrandisci la mappa (zoom \u2265 8) per vedere le navi."
                    : <>
                        {`${aisInfo.count} navi nell'area \u00B7 di cui ${aisInfo.service || 0} di servizio`}<br />
                        <span className="text-[#22c55e]">{"\u25CF"}</span> diporto <span className="text-[#facc15]">{"\u25CF"}</span> commerciali/sconosciute <span className="text-[#3b82f6]">{"\u25CF"}</span> servizio
                      </>}
                </div>
                <label className="flex items-center justify-between gap-2 text-xs text-white cursor-pointer" data-testid="ais-filter-row">
                  <span>Solo unità di servizio</span>
                  <Switch checked={aisOnlyService} onCheckedChange={setAisOnlyService} data-testid="ais-only-service" className="scale-90" />
                </label>
              </div>
            )}
          </div>

          {/* Traffico aereo ADS-B (single-row toggle) */}
          <div className="w-full bg-[#0A2647]/95 border border-[#1E5AA8] rounded-sm shadow-xl text-white"
            data-testid="flights-control">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="flex items-center gap-2 font-heading font-bold text-sm">
                <Plane className="h-4 w-4 text-[#22c55e]" /> Traffico aereo
              </span>
              <Switch checked={showFlights} onCheckedChange={setShowFlights} data-testid="flights-enable" />
            </div>
            {showFlights && (
              <div className="px-3 pb-2 -mt-0.5 space-y-1.5">
                <div className="text-[10px] text-[#A3C2E0] leading-tight" data-testid="flights-status">
                  {flightsInfo.error
                    ? "Dati ADS-B momentaneamente non disponibili."
                    : `${flightsInfo.count} aeromobili nell'area \u00B7 `}
                  {!flightsInfo.error && (
                    <span><span className="text-[#22c55e]">{"\u25CF"}</span> &lt;1000 m <span className="text-[#ef4444]">{"\u25CF"}</span> &gt;1000 m <span className="text-[#3b82f6]">{"\u25CF"}</span> soccorso/Stato</span>
                  )}
                </div>
                <label className="flex items-center justify-between gap-2 text-xs text-white cursor-pointer" data-testid="flights-filter-row">
                  <span>Solo velivoli di servizio</span>
                  <Switch checked={flightsOnlySpecial} onCheckedChange={setFlightsOnlySpecial} data-testid="flights-only-special" className="scale-90" />
                </label>
              </div>
            )}
          </div>

          {/* Motovedette (riepilogo unità) */}
          <div className="w-full bg-[#0A2647]/95 border border-[#1E5AA8] rounded-sm shadow-xl text-white"
            data-testid="moto-control">
            <button type="button" onClick={() => setMotoPanelOpen((o) => !o)} data-testid="moto-panel-toggle"
              className="w-full flex items-center justify-between px-3 py-2">
              <span className="flex items-center gap-2 font-heading font-bold text-sm">
                <Ship className="h-4 w-4 text-[#0EA5E9]" /> Motovedette
                <span className="font-mono text-[10px] text-[#A3C2E0]" data-testid="moto-panel-count">
                  {onlineMotovedette.length}/{motovedette.length} online
                </span>
                {motovedette.length > 0 && (
                  <span className="flex items-center gap-1 text-[10px] font-mono whitespace-nowrap" data-testid="moto-panel-free-count">
                    <span className="px-1 rounded-sm bg-[#2E7D32]/25 text-[#7ee08a] border border-[#2E7D32]/60" title="Libere">{motovedette.length - assignedMotoIds.size} lib.</span>
                    <span className="px-1 rounded-sm bg-[#E8B923]/20 text-[#E8B923] border border-[#E8B923]/60" title="Assegnate">{assignedMotoIds.size} ass.</span>
                  </span>
                )}
              </span>
              {motoPanelOpen ? <ChevronDown className="h-4 w-4 text-[#A3C2E0]" /> : <ChevronUp className="h-4 w-4 text-[#A3C2E0]" />}
            </button>
            {motoPanelOpen && (
              <div className="border-t border-[#1E5AA8]/50 max-h-[40vh] overflow-y-auto" data-testid="moto-panel-list">
                {motovedette.length === 0 && (
                  <p className="px-3 py-2 text-xs text-[#A3C2E0]">Nessuna motovedetta registrata.</p>
                )}
                {sortedMotovedette.map((mv) => {
                  const age = motoAgeMs(mv);
                  const online = age < MOTO_ONLINE_MS;
                  const recent = age < MOTO_ASSIGNABLE_MS;
                  const assigned = events.find((e) => e.assigned_motovedetta_id === mv.id);
                  const dot = online ? "bg-[#2E7D32]" : (recent ? "bg-[#E8B923]" : "bg-[#6f8fb3]");
                  const stateTxt = online ? "Online" : (recent ? `${Math.round(age / 60000)} min fa` : "Offline");
                  const rowTone = assigned
                    ? "border-l-[#E8B923] bg-[#E8B923]/10 hover:bg-[#E8B923]/20"
                    : "border-l-[#2E7D32] bg-[#2E7D32]/10 hover:bg-[#2E7D32]/20";
                  return (
                    <button key={mv.id} type="button" data-testid={`moto-row-${mv.id}`}
                      data-state={assigned ? "assigned" : "free"}
                      disabled={!mv.last_pos}
                      onClick={() => { const m = window.__sgcMap; if (m && mv.last_pos) m.setView([mv.last_pos.lat, mv.last_pos.lng], Math.max(m.getZoom(), 10)); }}
                      className={`w-full text-left px-3 py-1.5 border-b border-[#1E5AA8]/30 border-l-4 ${rowTone} disabled:opacity-60 disabled:cursor-default`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 text-xs font-semibold truncate">
                          <span className={`h-2 w-2 rounded-full shrink-0 ${dot}`} /> {mv.username}
                        </span>
                        <span data-testid={`moto-state-${mv.id}`}
                          className={`text-[10px] font-mono font-semibold shrink-0 px-1.5 py-0.5 rounded-sm border ${assigned
                            ? "text-[#E8B923] border-[#E8B923]/60 bg-[#E8B923]/15"
                            : "text-[#7ee08a] border-[#2E7D32]/60 bg-[#2E7D32]/20"}`}>
                          {assigned ? `Assegnata #${assigned.number}` : "Libera"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 text-[10px] text-[#A3C2E0] mt-0.5">
                        <span className="truncate">{mv.zone_name}</span>
                        <span className="font-mono shrink-0">{stateTxt}</span>
                      </div>
                      {mv.last_pos && (
                        <div className="text-[10px] font-mono text-[#A3C2E0]">{formatCoords(mv.last_pos.lat, mv.last_pos.lng)}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* status legend (bottom-right) */}
        <div className="absolute bottom-4 right-4 z-[1100] bg-[#0A2647]/90 border border-[#1E5AA8] rounded-sm px-3 py-2 text-xs" data-testid="map-legend">
          <div className="flex items-center gap-2 text-white"><span className="h-2.5 w-2.5 rounded-full bg-[#D32F2F]" /> Attivo (non preso)</div>
          <div className="flex items-center gap-2 text-white mt-1"><span className="h-2.5 w-2.5 rounded-full bg-[#E8B923]" /> In carico</div>
        </div>

        {!loading && !loadError && events.length === 0 && (
          <div className="absolute bottom-24 right-4 z-[1100] bg-[#113259] border border-[#1E5AA8] rounded-sm px-4 py-3 text-sm text-[#A3C2E0]"
            data-testid="dashboard-empty">
            <Anchor className="h-4 w-4 inline mr-1" /> Nessuna emergenza attiva
          </div>
        )}

        {openIds.length > 0 && (
          <div className="absolute top-4 right-4 z-[1100] flex flex-col gap-2 w-80 max-w-[90vw]"
            data-testid="call-stack">
            {activeEvent && (
              <CallerPanel
                event={activeEvent}
                call={activeCall}
                onMuteRole={call.setMuteRole}
                onToggleMic={call.toggleMic}
                onMinimize={minimizePanel}
                onAssign={(ev) => setAssignEvent(ev)}
                nearby={nearbyCtx}
                routeInfo={routeInfo}
                onClose={() => closePanel(activeEvent.id)}
                onEndRequest={(ev) => setConfirmEnd(ev)}
              />
            )}
            {minimizedEvents.map((ev) => (
              <MinimizedPanel key={ev.id} event={ev} call={callByEventId[ev.id]}
                onExpand={() => activatePanel(ev.id)}
                onClose={() => closePanel(ev.id)} />
            ))}
          </div>
        )}
      </div>

      <AlarmPanel events={pendingAlarms} onTake={handleTake} takingId={takingId}
        onDismiss={(ev) => setAlarmDismissed((d) => ({ ...d, [ev.id]: true }))} />

      {!pendingAlarms.length && callEndedEvent && (
        <CallEndedModal event={callEndedEvent} onClose={handleCloseFromCallEnded}
          onContinue={handleContinue} busy={busy} />
      )}

      <ChangePasswordDialog open={showPw} onOpenChange={setShowPw} />

      <AssignMotovedettaDialog event={assignEvent} motovedette={assignableMotovedette} onlineIds={onlineMotoIds}
        assignedTo={assignedToByMoto}
        onAssign={handleAssign} onClose={() => setAssignEvent(null)} busy={assigning} />

      <AlertDialog open={!!confirmEnd} onOpenChange={(o) => !o && setConfirmEnd(null)}>
        <AlertDialogContent className="bg-[#1E293B] border border-[#D32F2F] text-white rounded-sm" data-testid="confirm-end-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Confermare termine emergenza?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A3C2E0]">
              L'evento verrà chiuso e spostato in archivio. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-end-cancel"
              className="bg-transparent border-[#1E5AA8] text-white hover:bg-[#1E5AA8] rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doEndEmergency} disabled={busy} data-testid="confirm-end-confirm"
              className="bg-[#D32F2F] hover:bg-[#b71c1c] text-white rounded-sm">Termine emergenza</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
